<template>
  <div class="checkout-return">
    <div class="checkout-return__card">
      <div class="checkout-return__logo"><auth-logo /></div>
      <template v-if="state === 'waiting'">
        <div class="checkout-return__spinner"></div>
        <h1 class="pop-auth__h1" style="font-size: 24px">Finalizing your plan</h1>
        <p class="pop-auth__sub">This usually takes a few seconds.</p>
      </template>
      <template v-else-if="state === 'expired'">
        <h1 class="pop-auth__h1" style="font-size: 24px">That checkout expired</h1>
        <p class="pop-auth__sub">Nothing was charged. Your plan choice is saved.</p>
        <router-link class="pop-auth__btn pop-auth__btn--primary checkout-return__btn" to="/plans/checkout">Back to checkout</router-link>
      </template>
      <template v-else>
        <h1 class="pop-auth__h1" style="font-size: 24px">Payment received</h1>
        <p class="pop-auth__sub">We couldn't confirm the activation yet. It can take a minute; your plan unlocks on its own.</p>
        <button type="button" class="pop-auth__btn pop-auth__btn--primary checkout-return__btn" @click="finish(false)">Open my account</button>
        <p class="pop-auth__fine" style="margin-top: 14px">Still locked after a few minutes? <a class="pop-auth__link" href="mailto:support@pageoptimizer.pro">Contact support</a></p>
      </template>
    </div>
  </div>
</template>

<script>
import AuthLogo from '../AuthLogo.vue'
import { afterPlanRoute } from '../first-run'
import { segmentTrack } from '@/utils'

const POLL_MS = 1500
const MAX_WAIT_MS = 30000

// Stripe sends the browser back here (through the backend redirect, which adds ?sessionId=).
// The webhook normally activates the plan before we ask; the status endpoint catches up otherwise.
export default {
  name: 'PlanCheckoutReturnPage',
  components: { AuthLogo },
  data() {
    return { state: 'waiting', timer: null, startedAt: Date.now() }
  },
  computed: {
    sessionId() {
      return this.$route.query.sessionId || this.$store.state.signup.checkoutSessionId || ''
    },
  },
  mounted() {
    if (!this.sessionId) {
      this.finish(false)
      return
    }
    this.poll()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    async poll() {
      try {
        const response = await this.$store.dispatch('getCheckoutStatus', { sessionId: this.sessionId })
        const data = response.data || {}
        if (data.status === 'SUCCESS' && data.activated) {
          if (data.user) this.$store.commit('setUser', data.user)
          this.finish(true)
          return
        }
        if (data.state === 'expired' || data.action === 'UNKNOWN_SESSION') {
          this.state = 'expired'
          return
        }
      } catch (e) {
        console.log(e)
      }
      if (Date.now() - this.startedAt > MAX_WAIT_MS) {
        this.state = 'timeout'
        return
      }
      this.timer = setTimeout(this.poll, POLL_MS)
    },
    firePurchaseEvents() {
      const plan = this.$store.state.signup.plan || {}
      try {
        segmentTrack('purchase', { servicePackage: plan.code, price: plan.price, promoCode: this.$store.state.signup.promoCode, method: 'stripe' })
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({ event: 'purchase', transaction_id: this.sessionId, ecommerce: { value: plan.price, currency: 'USD', items: [{
          item_id: plan.code, item_name: `POP ${plan.title}`, item_category: plan.interval === 'year' ? 'Yearly' : 'Monthly',
          price: plan.price, discount: 0 }] } })
      } catch (e) {
        console.log(e)
      }
    },
    async finish(activated) {
      if (activated) this.firePurchaseEvents()
      try {
        const response = await this.$store.dispatch('loadUser')
        if (response && response.data && response.data.user) this.$store.commit('setUser', response.data.user)
      } catch (e) {
        console.log(e)
      }
      // The prefilled first run when one was saved from the example report, else the dashboard.
      // Only once per checkout: coming back to this page (history, reload) must not re-route, and
      // the page itself is replaced in history so "back" from the new-project form skips it.
      let target = afterPlanRoute(this.$store)
      if (typeof target === 'object') {
        const key = 'pop.firstRunRouted'
        let routed = ''
        try { routed = sessionStorage.getItem(key) || '' } catch (e) { /* storage blocked */ }
        if (routed === this.sessionId) {
          target = '/sites'
        } else {
          try { sessionStorage.setItem(key, this.sessionId) } catch (e) { /* storage blocked */ }
        }
      }
      this.$store.commit('signup/reset')
      this.$router.replace(target)
    },
  },
}
</script>

<style scoped lang="scss">
@import '../auth-tokens.scss';

.checkout-return {
  min-height: 100vh; background: $pop-page-bg; display: flex; align-items: center; justify-content: center; padding: 24px;
  color: $pop-teal; font-family: 'Mulish', sans-serif;

  &__card {
    width: 100%; max-width: 480px; background: #fff; border-radius: 20px; padding: 40px; text-align: center;
    box-shadow: $auth-card-shadow;
  }
  &__logo { display: flex; justify-content: center; margin-bottom: 30px; }
  &__spinner {
    width: 44px; height: 44px; margin: 0 auto 22px; border-radius: 50%;
    border: 4px solid $pop-field-border; border-top-color: $pop-accent; animation: checkout-return-spin 0.9s linear infinite;
  }
  &__btn { display: inline-flex; width: auto; padding: 13px 22px; text-decoration: none; }
}
@keyframes checkout-return-spin { to { transform: rotate(360deg); } }
</style>
